import { Injectable } from '@angular/core';
import { UiSession } from '@brighthub/uisession';
import {
  EnrichedQueueRow,
  HandoffState,
  InteractionChannel,
  PresenceState,
  UrgencyBadge,
} from './client-queue.model';

/**
 * ClientQueueEnrichmentService
 *
 * Merges real UiSession records (from the API) with deterministic mock
 * operational metadata for fields that are not yet available from the backend.
 *
 * ## Mock enrichment behavior
 * All generated values are derived from a djb2 hash of the session's
 * `uiSessionId`. This guarantees that the same session always produces the
 * same enriched values within a runtime, so the UI remains visually stable
 * across re-renders and filter changes.
 *
 * ## Fields that are mocked (no real API backing)
 * - presence state (active / recent / offline)
 * - lastActiveMinutesAgo
 * - lastChannel (email / sms / call / chat)
 * - lastChannelMinutesAgo
 * - waitingMinutes
 * - urgency badge
 * - handoff state (ai_in_progress / needs_fa / escalated / null)
 * - hasUpcomingCall / upcomingCallAt
 *
 * ## Known demo assumptions
 * - No real SLA or presence API exists; all operational metadata is generated.
 * - Scores and buckets are derived from generated data, not real service state.
 * - The scoring model is transparent and documented in ClientQueuePriorityEngine.
 */
@Injectable()
export class ClientQueueEnrichmentService {

  /**
   * Enrich a list of real UiSessions with deterministic mock metadata.
   * The `priorityScore`, `bucket`, `rankReasons`, and `stableIndex` fields
   * are left at their zero/default values — they are filled in by the
   * priority engine after enrichment.
   */
  enrich(sessions: UiSession[]): EnrichedQueueRow[] {
    return sessions.map((session) => this._enrichOne(session));
  }

  private _enrichOne(session: UiSession): EnrichedQueueRow {
    const id = session.uiSessionId;

    const presence = this._presence(id);
    const lastActiveMinutesAgo = this._lastActiveMinutes(id, presence);
    const lastChannel = this._lastChannel(id);
    const lastChannelMinutesAgo = this._lastChannelMinutes(id);
    const waitingMinutes = this._waitingMinutes(id);
    const urgency = this._urgency(waitingMinutes, id);
    const handoff = this._handoff(id);
    const hasUpcomingCall = this._hasUpcomingCall(id);
    const upcomingCallAt = hasUpcomingCall ? this._upcomingCallAt(id) : null;

    return {
      session,
      presence,
      lastActiveMinutesAgo,
      lastChannel,
      lastChannelMinutesAgo,
      waitingMinutes,
      urgency,
      handoff,
      hasUpcomingCall,
      upcomingCallAt,
      // Filled in by priority engine:
      priorityScore: 0,
      bucket: 'later',
      rankReasons: [],
      stableIndex: 0,
    };
  }

  // ---------------------------------------------------------------------------
  // Deterministic seeding helpers
  // ---------------------------------------------------------------------------

  /** djb2 hash — same pattern used across all state services in this codebase. */
  private _djb2(str: string): number {
    let hash = 5381;
    for (let i = 0; i < str.length; i++) {
      hash = (hash * 33) ^ str.charCodeAt(i);
    }
    return hash >>> 0;
  }

  private _seedInt(id: string, key: string, min: number, max: number): number {
    const h = this._djb2(`${id}:${key}`);
    return min + (h % (max - min + 1));
  }

  private _seedPick<T>(id: string, key: string, options: readonly T[]): T {
    const h = this._djb2(`${id}:${key}`);
    return options[h % options.length];
  }

  // ---------------------------------------------------------------------------
  // Field generators
  // ---------------------------------------------------------------------------

  private _presence(id: string): PresenceState {
    return this._seedPick(id, 'presence', ['active', 'active', 'recent', 'recent', 'offline'] as const);
  }

  private _lastActiveMinutes(id: string, presence: PresenceState): number {
    if (presence === 'active') return this._seedInt(id, 'lastActiveActive', 0, 2);
    if (presence === 'recent') return this._seedInt(id, 'lastActiveRecent', 3, 45);
    return this._seedInt(id, 'lastActiveOffline', 60, 480);
  }

  private _lastChannel(id: string): InteractionChannel {
    return this._seedPick(id, 'channel', ['email', 'sms', 'call', 'chat'] as const);
  }

  private _lastChannelMinutes(id: string): number {
    return this._seedInt(id, 'channelMins', 1, 120);
  }

  private _waitingMinutes(id: string): number {
    // Weighted toward shorter waits, with some long outliers for demo interest
    const bucket = this._seedInt(id, 'waitBucket', 0, 9);
    if (bucket <= 3) return this._seedInt(id, 'waitShort', 0, 8);
    if (bucket <= 6) return this._seedInt(id, 'waitMed', 9, 22);
    return this._seedInt(id, 'waitLong', 23, 60);
  }

  private _urgency(waitingMinutes: number, id: string): UrgencyBadge {
    if (waitingMinutes > 30) return 'overdue';
    if (waitingMinutes > 15) return 'due_soon';
    const special = this._seedPick(id, 'urgencySpecial', [false, false, false, true] as const);
    if (special) return 'waiting_on_client';
    return 'on_track';
  }

  private _handoff(id: string): HandoffState {
    return this._seedPick(
      id,
      'handoff',
      [null, null, null, null, 'ai_in_progress', 'ai_in_progress', 'needs_fa', 'escalated'] as const
    );
  }

  private _hasUpcomingCall(id: string): boolean {
    return this._seedPick(id, 'hasCall', [false, false, false, true] as const);
  }

  private _upcomingCallAt(id: string): string {
    const minutesFromNow = this._seedInt(id, 'callMins', 10, 55);
    const d = new Date(Date.now() + minutesFromNow * 60 * 1000);
    return d.toISOString();
  }
}
